import { Link } from "react-router-dom";
import { ArrowRight, ClipboardList, Stethoscope, PackageCheck, Repeat } from "lucide-react";
import AbstractShape from "@/components/AbstractShape";
import { goToIntake } from "@/lib/intake";
import useSEO from "@/lib/useSEO";
import { breadcrumbSchema } from "@/lib/schema";

const STEPS = [
  {
    icon: ClipboardList,
    title: "Choose your wellness goal.",
    body: "Browse five focused treatment categories built around real wellness goals: Energy, Antioxidant, Vitality, Metabolic Support, or Weight Loss.",
  },
  {
    icon: ArrowRight,
    title: "Get started in minutes.",
    body: "Begin your secure online intake. No medical information is required to start.",
  },
  {
    icon: Stethoscope,
    title: "Complete your secure online health intake.",
    body: "You'll complete a structured online health intake through a secure clinical intake platform, separate from this marketing site.",
  },
  {
    icon: PackageCheck,
    title: "A licensed clinician reviews your information.",
    body: "A clinician reviews your health history, medications, allergies, and contraindications. They may approve, deny, request more information, or require a brief live phone or video visit.",
  },
  {
    icon: Repeat,
    title: "If clinically appropriate, a prescription may be issued.",
    body: "When prescribed, a licensed pharmacy partner may dispense and ship to eligible patients. Refills require continued clinical appropriateness review.",
  },
];

export default function HowItWorks() {
  useSEO({
    title: "How Melbi Wellness Works | Online Clinician-Guided Care",
    description:
      "Choose your wellness goal, complete a secure online intake, and get clinician review with licensed pharmacy fulfillment when clinically appropriate.",
    url: "https://melbiwellness.com/how-it-works",
    jsonLd: [
      breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "How It Works", url: "/how-it-works" },
      ]),
    ],
  });
  return (
    <div data-testid="how-it-works-page">
      <section className="relative">
        <AbstractShape variant="gradient-cream" colors={["#F5E6D3", "#E8D5C4", "#FAF9F6"]} className="absolute inset-0" testId="how-hero-bg" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 md:pt-24 md:pb-16">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9A92]">How it works</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-[#2C2B29] max-w-3xl">
            Simple, guided, medically responsible.
          </h1>
          <p className="mt-5 text-[#5E5C58] text-lg leading-relaxed max-w-2xl">
            The Melbi patient experience — designed to be easy without ever cutting corners on clinical responsibility.
          </p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <ol className="space-y-5">
          {STEPS.map((s, i) => (
            <li
              key={s.title}
              data-testid={`how-step-${i + 1}`}
              className="bg-white rounded-3xl p-6 md:p-8 border border-[#E8E5DF] flex flex-col md:flex-row gap-5 md:gap-8"
            >
              <div className="shrink-0 flex md:flex-col md:items-start items-center gap-3">
                <span className="font-display text-4xl md:text-5xl font-bold text-[#C96A4E] leading-none">
                  0{i + 1}
                </span>
                <span className="hidden md:flex w-12 h-12 rounded-full bg-[#F3F1EC] items-center justify-center">
                  <s.icon size={20} className="text-[#8A9A92]" />
                </span>
              </div>
              <div>
                <h3 className="font-display text-xl md:text-2xl font-semibold tracking-tight text-[#2C2B29]">
                  {s.title}
                </h3>
                <p className="mt-3 text-[#5E5C58] leading-relaxed">{s.body}</p>
              </div>
            </li>
          ))}
        </ol>

        <div className="mt-12 text-center">
          <button
            data-testid="how-start-now-cta"
            className="inline-flex items-center justify-center bg-[#C96A4E] text-white rounded-full px-8 py-4 text-base font-semibold hover:bg-[#B35A40] transition-colors shadow-sm" type="button" onClick={goToIntake}>
            Start now
            <ArrowRight size={18} className="ml-2" />
          </button>
          <p className="mt-3 text-xs text-[#5E5C58]">
            Clinical review is required. Not all patients qualify.
          </p>
        </div>
      </section>
    </div>
  );
}
